import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Scale } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { getDisplayPrice } from '@/utils/pricing';

const KEY = 'impactgolf_compare';
const EVT = 'compare-change';
const MAX = 3;

function read() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch {
    return [];
  }
}

function write(list) {
  localStorage.setItem(KEY, JSON.stringify(list));
  window.dispatchEvent(new Event(EVT));
}

export function useCompare() {
  const [items, setItems] = useState(read);
  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const toggle = useCallback((product) => {
    const list = read();
    const exists = list.find((p) => p.product_id === product.product_id);
    let next;
    if (exists) {
      next = list.filter((p) => p.product_id !== product.product_id);
    } else if (list.length >= MAX) {
      return 'full';
    } else {
      next = [
        ...list,
        {
          product_id: product.product_id,
          name: product.name,
          manufacturer: product.manufacturer,
          image_url: product.image_url,
          min_advertised_price: getDisplayPrice(product),
          category: product.category,
        },
      ];
    }
    write(next);
    return exists ? 'removed' : 'added';
  }, []);
  const clear = useCallback(() => write([]), []);
  return { items, toggle, clear, has: (id) => items.some((p) => p.product_id === id), MAX };
}

/** Floating bottom bar that appears when there are compare items. */
export default function CompareBar() {
  const { items, toggle, clear } = useCompare();
  const { tenantPath } = useTenant();
  if (items.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white rounded-full shadow-2xl border border-gray-200 pl-2 pr-4 py-2 flex items-center gap-3 animate-in slide-in-from-bottom-4 duration-200"
      data-testid="compare-bar"
    >
      <div className="flex items-center gap-1.5">
        {items.map((p) => (
          <div
            key={p.product_id}
            className="relative w-10 h-10 bg-gray-100 rounded-full overflow-hidden border border-gray-200"
            title={p.name}
          >
            {p.image_url && (
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            )}
            <button
              onClick={() => toggle(p)}
              className="absolute -top-0.5 -right-0.5 bg-gray-800 text-white rounded-full w-4 h-4 flex items-center justify-center"
              aria-label={`Remove ${p.name}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        ))}
      </div>
      <span className="text-xs text-gray-600 font-medium">
        {items.length} / 3
      </span>
      <Link
        to={tenantPath('/compare')}
        className="inline-flex items-center gap-1.5 bg-[#334155] text-white text-sm font-semibold rounded-full px-4 py-1.5 hover:bg-[#475569] transition-colors"
        data-testid="compare-open"
      >
        <Scale className="w-4 h-4" /> Compare
      </Link>
      <button
        onClick={clear}
        className="text-xs text-gray-500 hover:text-gray-800"
        data-testid="compare-clear"
      >
        Clear
      </button>
    </div>
  );
}
